import { FC, useEffect, useState } from "react";
import { DocumentGridStateType, ISaveFocus } from "../../../../@types/documents";
import { DocumentStatusType, DocumentType, IdTableVariant, LoadingStatus } from "../../../../@types/enumsGlobal";
import { DefaultGrid } from "../../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../../components/plugins";
import { Spinner } from "../../../../components/spiner/Spinner";
import { TabsPanel } from "../../../../components/tabs";
import useGridFilter, { DefaultDateGridFilter } from "../../../../system/hooks/useGridFilter";
import { useDetailsTabsPanel } from "../../../../system/hooks/useTabsPanel";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../../system/providers/plugin";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import styles from "../../../styles/index.module.scss";
import tabsStyles from "../../../../pages/styles/homePage.module.scss";
import classNames from "classnames";
import { UserActionLogModal } from "../../../Service/UserActionLog/view/UserActionLogModal";
import { InventorySvedDataProvider } from "../../../../Services/DataProviders/InventorySvedDataProvider";
import { IInventorySvedViewDTO } from "../../../../libs/coreapi-dto/documents/invoice/inventory/inventorySved";
import { useTranslation } from "react-i18next";
import DefaultDocumentsCommandsPanelV3 from "../../../../components/commandsPanels/DefaultDocumentsCommandsPanelV3";
import { ConfirmationItemsModal } from "../../../../components/modalWindows/ConfirmationItemsModal";
import { AllowDTO } from "../../../../libs/coreapi-dto/dirs/allow";
import { ProgressActionSpinner } from "../../../../components/progressActionSpinner/ProgressActionSpinner";



export const InventorySvedPluginView: FC<IGridProps> = (props) => {
    const appContext = useAppContext();
    const pluginCtx = usePluginContext();
    const tabCtx = useTabsContext();
    const inventorySvedDP = new InventorySvedDataProvider(appContext.coreApiService);

    const [viewState, setViewState] = useState<DocumentGridStateType>("view");
    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultDateGridFilter());
    const [data, setData] = useState<IInventorySvedViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.Completed);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [showDetailsTabs, setShowDetailsTabs] = useState<boolean>(false);
    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);
    const [documentStatusType, setDocumentStatusType] = useState<DocumentStatusType | undefined>();
    const [selectedDocumentState, setSelectedDocumentState] = useState<DocumentStatusType | undefined>();
    const [multipleSelect, setMultipleSelect] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItemView, setSelectedItemViews] = useState<IInventorySvedViewDTO>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);

    // Modal Log
    const [showModalLog, setShowModalLog] = useState<boolean>(false);

    // Локализация
    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.inventorySved." + value);

    const [saveFocus, setSaveFocus] = useState<ISaveFocus>({
        viewState: 'view',
        selectedItem: selectedItem,
        data: data
    });

    useEffect(() => {
        if (viewState === "view") {
            setIsSubmitting(true);
            inventorySvedDP.getView(gridFilter, async (data, totalCount) => {
                setData(data);
                setTotalCount(totalCount);
                setIsSubmitting(false);
                setLoadingStatus(totalCount > 0 ? LoadingStatus.Completed : LoadingStatus.NoData);
            });
        }

        if (viewState === "refresh") {
            setViewState("view");
        }
    }, [gridFilter, viewState]);

    useEffect(() => {
        if (tabCtx.currentTab?.title === `${baseT("tabName")}`) {
            setShowDetailsTabs(true);
        } else {
            setShowDetailsTabs(false);
        }
        pluginCtx.masterGrid.onUnselectEvent();
        setShowDetailsTabs(true);
    }, [tabCtx.currentTab]);

    useEffect(() => {
        if (!multipleSelect) {
            setSelectedItems([]);
        } else {
            setSelectedItem(undefined);
        }

        if (selectedItem) {
            const documentState = selectedItem.cells.find((c) => c.propertyName === "documentState")?.value as DocumentStatusType;
            setSelectedDocumentState(documentState ?? undefined);
            setSelectedItemViews(data.find((x) => x.idGlobal === selectedItem.idGlobal));
        } else {
            setSelectedItemViews(undefined);
        }
    }, [multipleSelect, selectedItem]);

    useEffect(() => {
        pluginCtx.masterGrid.onUnselectEvent();
    }, [documentStatusType, gridFilter, viewState]);

    const deleteRow = () => {
        if (selectedItem?.[0]?.isDeleted || selectedDocumentState === "save" || selectedDocumentState === "proc") {
            // anything else ... ups, I don't know why it's empty
        } else {
            inventorySvedDP.markDelete(selectedItem?.idGlobal as string, () => {
                setViewState("refresh");
            });
        }
    };

    const processPreliminaryHandleWithConfirmation = (idDocumentGlobal: string, sendData: () => void) => {
        renderProgressView("Проверка товарных движений");
        inventorySvedDP.checkLotMovements(idDocumentGlobal, (result: AllowDTO) => {
            setModalWindow(<></>)
            if (!result.isAllowed) {
                renderConfirmationModal(
                    result.title, 
                    result.details, 
                    result.items.join('\r\n'), 
                    result.question, 
                    () => setConfirmationItemsModalJsx(<></>),
                    () => {
                        sendData()
                        setConfirmationItemsModalJsx(<></>)
                    }
                )
            } else {
                sendData()
            }
        }, () => {
            setModalWindow(<></>)
            setConfirmationItemsModalJsx(<></>)
        })
    }

    const [сonfirmationItemsModalJsx, setConfirmationItemsModalJsx] = useState<JSX.Element>(<></>);
    const renderConfirmationModal = (header: string, warning: string, items: string, question, cancel: () => void, ok: () => void) => {
        setConfirmationItemsModalJsx(
            <ConfirmationItemsModal
                header={`${header}`}
                warning={`${warning}`}
                listPositions={`${items}`}
                question={`${question}`}
                cancel={cancel}
                largeTextArea
                ok={ok}
            />
        );
    }

    const [modalWindow, setModalWindow] = useState(<></>);
    const renderProgressView = (actionMessage: string) => {
        setModalWindow(
            <ProgressActionSpinner
                loaderText={`${actionMessage}`}
            />
        );
    };

    return (
        <>
            <PluginWrapper>
                <ToolbarWrapper>
                    <DefaultDocumentsCommandsPanelV3
                        selectedItems={[selectedItems,setSelectedItems]}
                        selectedItem={[selectedItem, setSelectedItem]}
                        saveFocus={[saveFocus, setSaveFocus]}
                        gridFilter={[gridFilter, dispatchGridFilter]}
                        plugin="inventory_sved_create_plugin"
                        documentStatusType={[documentStatusType, setDocumentStatusType]}
                        selectedDocumentStatusType={[selectedDocumentState, setSelectedDocumentState]}
                        multipleSelect={[multipleSelect, setMultipleSelect]}
                        dataProvider={inventorySvedDP}
                        pluginSettings={props.plugin}
                        idTable={IdTableVariant.InventorySved}
                        setViewState={(vs) => setViewState(vs)}
                        advancedFeatures
                        items={[{
                            label: t(`${t("general.showUserLog")}`),
                            onClick: () => selectedItem && setShowModalLog(true),
                            disabled: selectedItem ? false : true,
                        },]}
                        mnemocode={selectedItemView?.mnemocode}
                        processPreliminaryHandleWithConfirmation={processPreliminaryHandleWithConfirmation}
                    />
                </ToolbarWrapper>
                <div>
                    <div>
                        {isSubmitting ? (
                            <Spinner />
                        ) : (
                            <DefaultGrid
                                openWithEnterForEdit={{
                                    pluginTabContext: "inventory_sved_create_plugin",
                                    mnemocode: selectedItemView?.mnemocode,
                                    selectedDocumentState
                                }}
                                gridId={props.gridId}
                                data={data}
                                documentStatus
                                separator
                                loadingStatus={loadingStatus}
                                setLoadingStatus={setLoadingStatus}
                                actionGridRow={{ delete: () => deleteRow() }}
                                filter={gridFilter}
                                hiddenPagination={undefined}
                                dataProvider={inventorySvedDP}
                                totalCount={totalCount}
                                plugin={props.plugin}
                                multipleSelect={multipleSelect}
                                selectedItem={selectedItem}
                                selectedItems={selectedItems}
                                saveFocus={[saveFocus, setSaveFocus]}
                                onSelect={(row) => {
                                    setSelectedItem(row);
                                    row ? pluginCtx.masterGrid.onSelectEvent(row, DocumentType.actDeduction) : pluginCtx.masterGrid.onUnselectEvent();
                                }}
                                onMultipleSelect={(rows) => setSelectedItems(rows)}
                                onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                                onFilterDelete={(i) => {
                                    i.propertyName === "documentState" && setDocumentStatusType(undefined);
                                    dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName });
                                }}
                                onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                            />
                        )}
                    </div>
                    <div className={styles.detailsTabView}>
                        {showDetailsTabs && detailsTabsPanel.tabs.length > 0 && selectedItem && !multipleSelect && (
                            <>
                                <div className={tabsStyles.tabsPanelWrapper}>
                                    <div id="detailsTabsPanelWrapper" style={{ position: "relative", overflow: "hidden", width: "100%" }}>
                                        <TabsPanel
                                            id="detailsTabsPanel"
                                            activeId={detailsTabsPanel.currentTab?.id}
                                            tabs={detailsTabsPanel.tabs}
                                            onActive={(id) => dispatchDetailsTabsPanel({ type: "activate", payload: id })}
                                        ></TabsPanel>
                                    </div>
                                </div>
                                <div className={tabsStyles.contentWrapper}>
                                    {detailsTabsPanel.tabs.map((item) => {
                                        return (
                                            <div key={item.id}
                                                className={classNames(item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden)}>
                                                {item.view.content}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                    {showModalLog && <UserActionLogModal idDocumentGlobal={selectedItem?.idGlobal} onClick={() => setShowModalLog(false)} />}
                    {modalWindow}
                    {сonfirmationItemsModalJsx}
                </div>
            </PluginWrapper>
        </>
    );
};